/**
 * Pure copy + values for moderating a reported piece of Jaguar-Owned content
 * from the report page (Task P3) — a vouch, an owner note, an Ask reply, or
 * an Ask request. Kept dependency-free so the per-kind mapping (which table,
 * which select, what the buttons say, what a remove/restore actually writes)
 * is covered directly by contentModeration.test.ts rather than only
 * indirectly through ReportedContent's own component tests.
 *
 * Every kind but 'business_request' is a plain tombstone: `status` flips
 * between 'visible' and 'removed', and an admin can flip it back. A request
 * is one-way — its own before-update guard (backend/supabase/migrations/
 * 20260923120300_jaguar_owned_ask.sql) only ever lets an admin move status to
 * 'removed' with a non-empty removed_reason, never back to 'open' — so
 * `canRestore` is false and `needsReason` is true ONLY for a request.
 *
 * Selects mirror the FK names verified against pg_constraint in the local
 * Supabase stack. Each of the four tables has exactly one FK to `businesses`,
 * so that embed is never named. `business_requests` alone has more than one
 * FK into `profiles` (requester, chosen owner, recommender) — which is why
 * its `author` embed must name the requester FK explicitly; the others name
 * theirs too, for consistency, even though each has only one.
 */

export type ModeratedKind = 'business_vouch' | 'business_note' | 'business_reply' | 'business_request'

export interface KindSpec {
  resource: 'business_vouches' | 'business_owner_notes' | 'business_request_replies' | 'business_requests'
  select: string
  noun: 'vouch' | 'note' | 'reply' | 'request'
  /** False only for business_request — its update guard allows → removed only. */
  canRestore: boolean
  /** True only for business_request — removed_reason is required. */
  needsReason: boolean
  removeTitle: string
  removeBody: string
  restoreTitle: string
}

const VOUCH_SELECT =
  'id,business_id,relationship,tags,sentence,disclosure,status,created_at,' +
  'author:profiles!business_vouches_voucher_profile_fkey(name,class_year),' +
  'business:businesses(id,name)'

// Task P7 fix round 1 (20260923121050_jaguar_owned_note_reads.sql): read_at
// dropped from this select — it's now permanently null on business_owner_
// notes (read state moved to the owner-only business_owner_note_reads
// table, which this report-moderation query has no reason to join), and
// nothing in the admin UI ever rendered it, so it was dead weight that
// would have started reading as a false "never read" for every note.
const NOTE_SELECT =
  'id,business_id,body,status,created_at,' +
  'author:profiles!business_owner_notes_sender_profile_fkey(name,class_year),' +
  'business:businesses(id,name)'

const REPLY_SELECT =
  'id,request_id,business_id,kind,body,status,created_at,' +
  'author:profiles!business_request_replies_author_profile_fkey(name,class_year),' +
  'business:businesses(id,name)'

const REQUEST_SELECT =
  'id,category_slug,area,body,status,is_private,removed_reason,created_at,' +
  'author:profiles!business_requests_requester_profile_fkey(name,class_year)'

const KIND_SPECS: Record<ModeratedKind, KindSpec> = {
  business_vouch: {
    resource: 'business_vouches',
    select: VOUCH_SELECT,
    noun: 'vouch',
    canRestore: true,
    needsReason: false,
    removeTitle: 'Remove this vouch?',
    removeBody: 'Members stop seeing it right away. The member who wrote it still sees it, marked as removed.',
    restoreTitle: 'Restore this vouch?',
  },
  business_note: {
    resource: 'business_owner_notes',
    select: NOTE_SELECT,
    noun: 'note',
    canRestore: true,
    needsReason: false,
    removeTitle: 'Remove this note?',
    // Fix round 1, item 4: no mobile screen shows a member their own SENT
    // notes (unlike a vouch, which the sender always reads via useMyVouch) —
    // promising the sender a "marked as removed" view they can't actually
    // see was false. A removed note simply disappears for everyone but an
    // admin looking at the report it came from.
    removeBody: 'The owner stops seeing it right away.',
    restoreTitle: 'Restore this note?',
  },
  business_reply: {
    resource: 'business_request_replies',
    select: REPLY_SELECT,
    noun: 'reply',
    canRestore: true,
    needsReason: false,
    removeTitle: 'Remove this reply?',
    removeBody: 'Members stop seeing it on the request right away.',
    restoreTitle: 'Restore this reply?',
  },
  business_request: {
    resource: 'business_requests',
    select: REQUEST_SELECT,
    noun: 'request',
    canRestore: false,
    needsReason: true,
    removeTitle: 'Remove this request?',
    removeBody: "Members stop seeing the request and its replies. A removed request can't be restored.",
    restoreTitle: 'Restore this request?',
  },
}

/** Fix round 2, item 3: shared instead of duplicated in `ReportedContent.tsx`
 * and `useContentModeration.ts`. */
export function capitalize(s: string): string {
  return s.length ? s[0].toUpperCase() + s.slice(1) : s
}

/** Maps a report's target_type to its moderation spec — null for anything
 * that isn't Jaguar-Owned content (post, photo, business, avatar, profile,
 * job, message, or any unrecognized value). */
export function kindSpec(targetType: string): KindSpec | null {
  if (targetType === 'business_vouch' || targetType === 'business_note'
    || targetType === 'business_reply' || targetType === 'business_request') {
    return KIND_SPECS[targetType]
  }
  return null
}

/** Values to write for a remove — `{ status: 'removed' }`, plus a trimmed
 * `removed_reason` for a request (required by its own update guard). */
export function removeValues(kind: ModeratedKind, reason?: string): Record<string, unknown> {
  if (kind === 'business_request') {
    return { status: 'removed', removed_reason: (reason ?? '').trim() }
  }
  return { status: 'removed' }
}

/** Values to write for a restore. Never called for business_request — its
 * guard has no path back from 'removed'. */
export function restoreValues(): { status: 'visible' } {
  return { status: 'visible' }
}

/** The report's own resolution_note copy for a remove action. */
export function resolutionNote(kind: ModeratedKind, reason?: string): string {
  const noun = KIND_SPECS[kind].noun
  if (kind === 'business_request') {
    return `Removed the request: ${(reason ?? '').trim()}`
  }
  return `Removed the ${noun}.`
}
